import CloseIcon from "@mui/icons-material/Close";
import MovieIcon from "@mui/icons-material/Movie";
import TvIcon from "@mui/icons-material/Tv";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import axios from "../../../utils/axiosInstance";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import {
    formatAvailsPacificDateTime,
    parseAvailsDateValue,
} from "../../../utilities/helpers/formatUnix";
import { AvailsPlusAirtableSourceKey } from "./availsPlusAirtableSources";
import {
    ACTIVITY_TYPE_LABELS,
    AvailsPlusCatalogDetail,
    CatalogLicenseWindow,
    CatalogRelationships,
} from "./availsPlusDetailTypes";
import {
    AVAILS_DETAIL_DRAWER_COLORS,
    detailDrawerFormControlSx,
    detailDrawerLabelSx,
    detailDrawerRootSx,
    detailDrawerSectionTitleSx,
    detailDrawerTableSx,
    detailDrawerTabsSx,
    detailDrawerVerticalTabsSx,
    licenseWindowRowSx,
} from "./availsPlusDetailStyles";
import HierarchySidebar from "./HierarchySidebar";
import ReadOnlyDetailField, { ReadOnlyTableText } from "./ReadOnlyDetailField";
import DetailCheckboxField from "./DetailCheckboxField";
import {
    FORMAT_PROFILE_OPTIONS,
    LICENSE_TYPE_OPTIONS,
    BRANDING_RIGHTS_CHECKED_VALUE,
    HDR_CHECKED_VALUE,
    isBrandingRightsChecked,
    isHdrChecked,
} from "./availsPlusOptions";
import RokuIndicator, { RokuStatus } from "./RokuIndicator";
import SlateIndicator, { SlateStatus } from "./SlateIndicator";
import {
    formatSlateMismatchValue,
    getSlateFieldLabel,
    SlateMismatch,
} from "./slateUtils";

dayjs.extend(utc);
dayjs.extend(timezone);

const AVAILS_PACIFIC_TZ = "America/Los_Angeles";
const BASE = process.env.BASE_URL || "";
const DETAIL_URL = `${BASE}api/v1/cms/avails-plus/catalog`;
const UPDATE_URL = `${BASE}api/v1/cms/avails-plus/catalog`;

const TITLE_LEVEL_FIELDS = [
    "license_type",
    "format_profile",
    "branding_rights",
    "hdr",
    "license_rights_description",
] as const;

const LICENSE_RIGHTS_OPTIONS = ["", "SWP", "SDP"];

interface AvailsPlusDetailDrawerProps {
    open: boolean;
    refId: string | null;
    slateStatus?: SlateStatus;
    slateMismatches?: SlateMismatch[];
    rokuStatus?: RokuStatus;
    airtableSource: AvailsPlusAirtableSourceKey;
    canEdit: boolean;
    canSubmitToSlate?: boolean;
    submittingToSlate?: boolean;
    canSubmitToRoku?: boolean;
    submittingToRoku?: boolean;
    onClose: () => void;
    onNavigateRef: (refId: string) => void;
    onUpdated?: () => void | Promise<void>;
    onSubmitToSlate?: (refId: string) => void | Promise<void>;
    onSubmitToRoku?: (refId: string) => void | Promise<void>;
    onSlateStatusChange?: (status: SlateStatus, mismatches: SlateMismatch[]) => void;
    onRokuStatusChange?: (status: RokuStatus) => void;
}

function contentTypeIcon(contentType: string) {
    const type = contentType?.toLowerCase();
    if (type === "episode") return <TvIcon fontSize="small" />;
    if (type === "supplemental") return <VideoLibraryIcon fontSize="small" />;
    return <MovieIcon fontSize="small" />;
}

function toPacificDayjs(value: string | number): Dayjs | null {
    const parsed = parseAvailsDateValue(value);
    if (!parsed) return null;
    return dayjs(parsed).tz(AVAILS_PACIFIC_TZ);
}

function pacificDayjsToUtcIso(value: Dayjs | null): string {
    if (!value?.isValid()) return "";
    return value.tz(AVAILS_PACIFIC_TZ, true).utc().toISOString();
}

function canEditLicenseWindowDateTime(
    canEdit: boolean,
    status: CatalogLicenseWindow["status"]
): boolean {
    return canEdit && status !== "past";
}

function canEditLicenseWindowAnnounceDate(
    canEdit: boolean,
    status: CatalogLicenseWindow["status"]
): boolean {
    return canEditLicenseWindowDateTime(canEdit, status);
}

function licenseWindowKey(window: Pick<CatalogLicenseWindow, "window_index" | "playback_start_date" | "playback_end_date">): string {
    return `${window.window_index}:${String(window.playback_start_date)}:${String(window.playback_end_date)}`;
}

function applyPacificTimeOntoLockedDate(
    lockedInstant: string | number,
    timeValue: Dayjs | null
): Dayjs | null {
    const locked = toPacificDayjs(lockedInstant);
    if (!locked?.isValid() || !timeValue?.isValid()) return null;
    return locked
        .hour(timeValue.hour())
        .minute(timeValue.minute())
        .second(timeValue.second())
        .millisecond(0);
}

function coerceOptionalNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

function normalizeDetail(raw: Record<string, unknown>): AvailsPlusCatalogDetail {
    return {
        reference_id: String(raw.reference_id ?? raw.referenceId ?? ""),
        name: String(raw.name ?? ""),
        content_type: String(raw.content_type ?? raw.contentType ?? ""),
        licensor: String(raw.licensor ?? raw.licensorStudio ?? ""),
        license_type: String(raw.license_type ?? raw.licenseType ?? "SVOD"),
        format_profile: String(raw.format_profile ?? raw.formatProfile ?? "HD"),
        branding_rights: String(raw.branding_rights ?? raw.brandingRights ?? ""),
        hdr: String(raw.hdr ?? ""),
        license_rights_description: String(
            raw.license_rights_description ?? raw.licenseRightsDescription ?? ""
        ),
        season_number: coerceOptionalNumber(raw.season_number ?? raw.seasonNumber),
        episode_number: coerceOptionalNumber(raw.episode_number ?? raw.episodeNumber),
        episode_count: coerceOptionalNumber(raw.episode_count ?? raw.episodeCount),
        season_count: coerceOptionalNumber(raw.season_count ?? raw.seasonCount),
        series_ref_id: String(raw.series_ref_id ?? raw.seriesRefId ?? ""),
        season_ref_id: String(raw.season_ref_id ?? raw.seasonRefId ?? ""),
        series_title: String(raw.series_title ?? raw.seriesTitle ?? "").trim(),
        season_title: String(raw.season_title ?? raw.seasonTitle ?? "").trim(),
        version: Number(raw.version) || 0,
        license_windows: Array.isArray(raw.license_windows)
            ? (raw.license_windows as CatalogLicenseWindow[])
            : [],
        activity: Array.isArray(raw.activity) ? (raw.activity as AvailsPlusCatalogDetail["activity"]) : [],
        created_at: String(raw.created_at ?? raw.createdAt ?? ""),
        updated_at: String(raw.updated_at ?? raw.updatedAt ?? ""),
    };
}

function normalizeRelationships(raw: unknown): CatalogRelationships | null {
    if (!raw || typeof raw !== "object") return null;
    const data = raw as Record<string, unknown>;
    return {
        ...(raw as CatalogRelationships),
        assets: Array.isArray(data.assets)
            ? (data.assets as CatalogRelationships["assets"])
            : [],
        seasons: Array.isArray(data.seasons)
            ? (data.seasons as CatalogRelationships["seasons"])
            : undefined,
    };
}

const DETAIL_TAB = {
    CORE: "core",
    ASSETS: "assets",
    AVAILS: "avails",
    ACTIVITY: "activity",
} as const;

type DetailTab = (typeof DETAIL_TAB)[keyof typeof DETAIL_TAB];

function titleMayHaveHierarchy(detail: AvailsPlusCatalogDetail): boolean {
    const type = detail.content_type?.toLowerCase();
    return type === "movie" || type === "episode" || type === "supplemental";
}

function displayOptionalSelectValue(value: string | undefined): string {
    return value?.trim() ? value : "(blank)";
}

export default function AvailsPlusDetailDrawer({
    open,
    refId,
    slateStatus = null,
    slateMismatches = [],
    rokuStatus = null,
    airtableSource,
    canEdit,
    canSubmitToSlate = false,
    submittingToSlate = false,
    canSubmitToRoku = false,
    submittingToRoku = false,
    onClose,
    onNavigateRef,
    onUpdated,
    onSubmitToSlate,
    onSubmitToRoku,
    onSlateStatusChange,
    onRokuStatusChange,
}: AvailsPlusDetailDrawerProps) {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isDesktopSidebar = useMediaQuery(theme.breakpoints.up("md"));
    const sidebarTabsSx = isDesktopSidebar ? detailDrawerVerticalTabsSx : detailDrawerTabsSx;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState<AvailsPlusCatalogDetail | null>(null);
    const [relationships, setRelationships] = useState<CatalogRelationships | null>(null);
    const [relationshipsLoading, setRelationshipsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<DetailTab>(DETAIL_TAB.AVAILS);
    const [draft, setDraft] = useState<Partial<AvailsPlusCatalogDetail>>({});
    const [announceDrafts, setAnnounceDrafts] = useState<Record<string, Dayjs | null>>({});
    const [playbackStartDrafts, setPlaybackStartDrafts] = useState<Record<string, Dayjs | null>>({});
    const [playbackEndDrafts, setPlaybackEndDrafts] = useState<Record<string, Dayjs | null>>({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const detailLoadedRef = useRef(false);
    const onRokuStatusChangeRef = useRef(onRokuStatusChange);
    const activeDrawerRef = useRef({ open, refId, airtableSource });
    onRokuStatusChangeRef.current = onRokuStatusChange;
    activeDrawerRef.current = { open, refId, airtableSource };

    const fetchDetail = useCallback(async () => {
        if (!refId) return;
        const requestedRefId = refId;
        const requestedAirtableSource = airtableSource;
        const showFullLoader = !detailLoadedRef.current;
        if (showFullLoader) setLoading(true);
        try {
            const res = await axios.get(
                `${DETAIL_URL}/${encodeURIComponent(refId)}?airtableSource=${encodeURIComponent(airtableSource)}`
            );
            const normalized = normalizeDetail(res.data?.data ?? {});
            const nextRokuStatus = (res.data?.data as Record<string, unknown> | undefined)
                ?.roku_status;
            if (
                nextRokuStatus === "synced" ||
                nextRokuStatus === "require_sync" ||
                nextRokuStatus === "not_exist"
            ) {
                const activeDrawer = activeDrawerRef.current;
                if (
                    activeDrawer.open &&
                    activeDrawer.refId === requestedRefId &&
                    activeDrawer.airtableSource === requestedAirtableSource
                ) {
                    onRokuStatusChangeRef.current?.(nextRokuStatus as RokuStatus);
                }
            }
            setDetail(normalized);
            detailLoadedRef.current = true;
            setDraft({
                license_type: normalized.license_type,
                format_profile: normalized.format_profile,
                branding_rights: normalized.branding_rights,
                hdr: normalized.hdr,
                license_rights_description: normalized.license_rights_description,
            });
            const announces: Record<string, Dayjs | null> = {};
            const playbackStarts: Record<string, Dayjs | null> = {};
            const playbackEnds: Record<string, Dayjs | null> = {};
            normalized.license_windows.forEach((window) => {
                const key = licenseWindowKey(window);
                announces[key] = toPacificDayjs(window.coming_soon_date);
                playbackStarts[key] = toPacificDayjs(window.playback_start_date);
                playbackEnds[key] = toPacificDayjs(window.playback_end_date);
            });
            setAnnounceDrafts(announces);
            setPlaybackStartDrafts(playbackStarts);
            setPlaybackEndDrafts(playbackEnds);
        } catch (error) {
            console.error("Failed to load Avails+ detail", error);
            enqueueSnackbar("Failed to load title detail.", { variant: "error" });
            setDetail(null);
        } finally {
            if (showFullLoader) setLoading(false);
        }
    }, [airtableSource, enqueueSnackbar, refId]);

    const fetchRelationships = useCallback(async () => {
        if (!refId) return;
        setRelationshipsLoading(true);
        try {
            const res = await axios.get(
                `${DETAIL_URL}/${encodeURIComponent(refId)}/relationships?airtableSource=${encodeURIComponent(airtableSource)}`
            );
            const next = normalizeRelationships(res.data?.data);
            setRelationships((previous) => {
                if (next && ((next.seasons?.length ?? 0) > 0 || next.assets.length > 0)) {
                    return next;
                }
                // Keep the current tree when navigating to a related title whose own
                // relationships payload is empty (common for extras missing seriesRefId).
                if (
                    previous &&
                    (previous.seasons?.some((season) =>
                        season.assets.some((asset) => asset.ref_id === refId)
                    ) ||
                        previous.assets.some((asset) => asset.ref_id === refId))
                ) {
                    return previous;
                }
                return next;
            });
        } catch (error) {
            console.error("Failed to load Avails+ relationships", error);
            enqueueSnackbar("Failed to load title relationships.", { variant: "error" });
            setRelationships((previous) => {
                if (
                    previous &&
                    (previous.seasons?.some((season) =>
                        season.assets.some((asset) => asset.ref_id === refId)
                    ) ||
                        previous.assets.some((asset) => asset.ref_id === refId))
                ) {
                    return previous;
                }
                return null;
            });
        } finally {
            setRelationshipsLoading(false);
        }
    }, [airtableSource, enqueueSnackbar, refId]);

    useEffect(() => {
        if (open && refId) {
            detailLoadedRef.current = false;
            setActiveTab(DETAIL_TAB.AVAILS);
            fetchDetail();
            void fetchRelationships();
        }
        if (!open) {
            setDetail(null);
            setRelationships(null);
            detailLoadedRef.current = false;
            setConfirmOpen(false);
            setActiveTab(DETAIL_TAB.AVAILS);
        }
    }, [open, refId, fetchDetail, fetchRelationships]);

    const buildUpdatePayload = (applyToRelated: boolean): Record<string, unknown> | null => {
        if (!detail) return null;
        const payload: Record<string, unknown> = {
            version: detail.version,
            apply_to_related: applyToRelated,
        };

        let hasChanges = false;
        for (const field of TITLE_LEVEL_FIELDS) {
            if (draft[field] !== undefined && draft[field] !== detail[field]) {
                payload[field] = draft[field];
                hasChanges = true;
            }
        }

        const licenseWindowUpdates: Array<{
            window_index: number;
            coming_soon_date?: string;
            playback_start_date?: string;
            playback_end_date?: string;
        }> = [];

        for (const window of detail.license_windows) {
            if (window.status === "past") continue;
            const draftKey = licenseWindowKey(window);
            const update: {
                window_index: number;
                coming_soon_date?: string;
                playback_start_date?: string;
                playback_end_date?: string;
            } = { window_index: window.window_index };

            const draftAnnounce = announceDrafts[draftKey];
            const currentAnnounceIso = pacificDayjsToUtcIso(draftAnnounce);
            const existingAnnounceIso =
                toPacificDayjs(window.coming_soon_date)?.utc().toISOString() || "";
            if (currentAnnounceIso !== existingAnnounceIso) {
                update.coming_soon_date = currentAnnounceIso;
            }

            const draftStart = playbackStartDrafts[draftKey];
            const currentStartIso = pacificDayjsToUtcIso(draftStart);
            const existingStartIso =
                toPacificDayjs(window.playback_start_date)?.utc().toISOString() || "";
            if (currentStartIso && currentStartIso !== existingStartIso) {
                update.playback_start_date = currentStartIso;
            }

            const draftEnd = playbackEndDrafts[draftKey];
            const currentEndIso = pacificDayjsToUtcIso(draftEnd);
            const existingEndIso =
                toPacificDayjs(window.playback_end_date)?.utc().toISOString() || "";
            if (currentEndIso && currentEndIso !== existingEndIso) {
                update.playback_end_date = currentEndIso;
            }

            if (
                update.coming_soon_date !== undefined ||
                update.playback_start_date !== undefined ||
                update.playback_end_date !== undefined
            ) {
                licenseWindowUpdates.push(update);
            }
        }

        if (licenseWindowUpdates.length > 0) {
            payload.license_window_updates = licenseWindowUpdates;
            hasChanges = true;
        }

        return hasChanges ? payload : null;
    };

    const hasUnsavedChanges = () => Boolean(buildUpdatePayload(false));

    const submitUpdate = async (applyToRelated: boolean) => {
        if (!detail || !refId) return;
        const payload = buildUpdatePayload(applyToRelated);
        if (!payload) return;

        const previousSlateStatus = slateStatus;
        const touchedSlateFields = TITLE_LEVEL_FIELDS.some(
            (field) => payload[field] !== undefined
        ) || Boolean(payload.license_window_updates);

        setSaving(true);
        try {
            const res = await axios.patch(
                `${UPDATE_URL}/${encodeURIComponent(refId)}?airtableSource=${encodeURIComponent(airtableSource)}`,
                payload
            );
            const data = (res.data?.data ?? {}) as Record<string, unknown>;
            const nextSlateStatus = data.slate_status ?? data.slateStatus;
            if (
                nextSlateStatus === "synced" ||
                nextSlateStatus === "require_sync" ||
                nextSlateStatus === "not_exist"
            ) {
                const nextMismatches = Array.isArray(data.slate_mismatches)
                    ? (data.slate_mismatches as SlateMismatch[])
                    : Array.isArray(data.slateMismatches)
                      ? (data.slateMismatches as SlateMismatch[])
                      : [];
                onSlateStatusChange?.(nextSlateStatus as SlateStatus, nextMismatches);
            } else if (
                touchedSlateFields &&
                (previousSlateStatus === "synced" || previousSlateStatus === "not_exist")
            ) {
                // Optimistic: catalog diverged from cached Slate until the next sync.
                onSlateStatusChange?.("require_sync", []);
            }
            if (touchedSlateFields && rokuStatus === "synced") {
                // Roku has no status API: any catalog edit after a submit
                // invalidates the stored signature until the next Submit to Roku.
                onRokuStatusChange?.("require_sync");
            }
            await fetchDetail();
            enqueueSnackbar("Catalog updated.", { variant: "success" });
            await onUpdated?.();
        } catch (error) {
            console.error("Avails+ detail update failed", error);
            enqueueSnackbar("Failed to update catalog.", { variant: "error" });
            if (
                typeof error === "object" &&
                error !== null &&
                "response" in error &&
                (error as { response?: { data?: { error?: { code?: string } } } }).response?.data
                    ?.error?.code === "AVAILS_CATALOG_CONFLICT"
            ) {
                fetchDetail();
            }
        } finally {
            setSaving(false);
            setConfirmOpen(false);
        }
    };

    const handleSave = () => {
        if (!detail) return;
        const payload = buildUpdatePayload(false);
        if (!payload) {
            enqueueSnackbar("No changes to save.", { variant: "info" });
            return;
        }

        const isEpisode = detail.content_type?.toLowerCase() === "episode";
        const titleLevelChanged = TITLE_LEVEL_FIELDS.some(
            (field) => payload[field] !== undefined
        );

        if (isEpisode && titleLevelChanged) {
            setConfirmOpen(true);
            return;
        }

        void submitUpdate(false);
    };

    const isEpisode = detail?.content_type?.toLowerCase() === "episode";
    const showHierarchy = detail ? titleMayHaveHierarchy(detail) : false;

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: { xs: "100%", md: "85%" },
                        maxWidth: "100%",
                        ...detailDrawerRootSx,
                    },
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%", ...detailDrawerRootSx }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            px: 3,
                            py: 2,
                            borderBottom: 1,
                            borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
                            ...detailDrawerRootSx,
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {detail && contentTypeIcon(detail.content_type)}
                            <Typography variant="h6">
                                {detail?.name || "Title detail"}
                            </Typography>
                            <SlateIndicator
                                status={slateStatus}
                                variant="chip"
                                showLabel
                                mismatches={slateMismatches}
                                referenceId={detail?.reference_id || refId || undefined}
                                title={detail?.name}
                                canSync={canSubmitToSlate && Boolean(onSubmitToSlate)}
                                syncing={submittingToSlate}
                                onSyncToSlate={
                                    refId && onSubmitToSlate
                                        ? () => onSubmitToSlate(refId)
                                        : undefined
                                }
                            />
                            <RokuIndicator status={rokuStatus} showLabel />
                        </Box>
                        <IconButton onClick={onClose} aria-label="Close detail drawer">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {slateStatus === "require_sync" && slateMismatches.length > 0 ? (
                        <Alert severity="warning" sx={{ mx: 3, mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Catalog differs from Prime Video Slate
                            </Typography>
                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {slateMismatches.map((mismatch) => (
                                    <Box component="li" key={mismatch.field} sx={{ mb: 0.5 }}>
                                        <Typography variant="body2">
                                            <strong>{getSlateFieldLabel(mismatch.field)}:</strong>{" "}
                                            catalog{" "}
                                            {formatSlateMismatchValue(mismatch.expected)}
                                            {" · "}Slate{" "}
                                            {formatSlateMismatchValue(mismatch.actual)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Alert>
                    ) : null}

                    {loading ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                ...detailDrawerRootSx,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : detail ? (
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                minHeight: 0,
                                ...detailDrawerRootSx,
                            }}
                        >
                            <Box
                                sx={{
                                    width: { xs: "100%", md: showHierarchy ? 440 : 180 },
                                    flexShrink: 0,
                                    alignSelf: "flex-start",
                                    maxHeight: "100%",
                                    overflowY: "auto",
                                    borderRight: { md: 1 },
                                    borderBottom: { xs: 1, md: 0 },
                                    borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
                                    display: "flex",
                                    flexDirection: "column",
                                    ...detailDrawerRootSx,
                                }}
                            >
                                {showHierarchy ? (
                                    <Box
                                        sx={{
                                            flexShrink: 0,
                                            maxHeight: { xs: 320, md: "50vh" },
                                            overflowY: "auto",
                                            px: 2,
                                            py: 2,
                                        }}
                                    >
                                        <HierarchySidebar
                                            relationships={relationships}
                                            loading={relationshipsLoading}
                                            currentRefId={detail.reference_id}
                                            currentTitle={detail.name}
                                            episodeNumber={detail.episode_number}
                                            contentType={detail.content_type}
                                            onNavigateRef={onNavigateRef}
                                        />
                                    </Box>
                                ) : null}

                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        borderTop: showHierarchy ? 1 : 0,
                                        borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
                                        px: { xs: 2, md: 1.5 },
                                        pt: 1.25,
                                        pb: 1.5,
                                    }}
                                >
                                    <Tabs
                                        orientation={isDesktopSidebar ? "vertical" : "horizontal"}
                                        value={activeTab}
                                        onChange={(_e, value: DetailTab) => setActiveTab(value)}
                                        variant="scrollable"
                                        scrollButtons={false}
                                        sx={sidebarTabsSx}
                                    >
                                        <Tab label="Core" value={DETAIL_TAB.CORE} disabled />
                                        <Tab label="Assets" value={DETAIL_TAB.ASSETS} disabled />
                                        <Tab label="Avails" value={DETAIL_TAB.AVAILS} />
                                        <Tab label="Activity" value={DETAIL_TAB.ACTIVITY} />
                                    </Tabs>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    flex: 1,
                                    display: "flex",
                                    flexDirection: "column",
                                    minWidth: 0,
                                    minHeight: 0,
                                    ...detailDrawerRootSx,
                                }}
                            >
                                <Box sx={{ px: 3, py: 2, ...detailDrawerRootSx }}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                sm: "1fr 1fr",
                                                md: "repeat(3, 1fr)",
                                            },
                                            gap: 2,
                                        }}
                                    >
                                        <ReadOnlyDetailField
                                            label={isEpisode ? "Episode RefID" : "Reference ID"}
                                            value={detail.reference_id}
                                        />
                                        <ReadOnlyDetailField
                                            label="Licensor / Studio"
                                            value={detail.licensor}
                                        />
                                        <ReadOnlyDetailField
                                            label="Content Type"
                                            value={detail.content_type}
                                        />
                                        {isEpisode && (
                                            <>
                                                <ReadOnlyDetailField
                                                    label="Series Title"
                                                    value={detail.series_title}
                                                />
                                                <ReadOnlyDetailField
                                                    label="Series Ref ID"
                                                    value={detail.series_ref_id}
                                                />
                                                <ReadOnlyDetailField
                                                    label="Season Number"
                                                    value={detail.season_number}
                                                />
                                                <ReadOnlyDetailField
                                                    label="Season Title"
                                                    value={detail.season_title}
                                                />
                                                <ReadOnlyDetailField
                                                    label="Season Ref ID"
                                                    value={detail.season_ref_id}
                                                />
                                                <ReadOnlyDetailField
                                                    label="Episode Number"
                                                    value={detail.episode_number}
                                                />
                                            </>
                                        )}
                                    </Box>
                                </Box>

                                <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2, ...detailDrawerRootSx }}>
                                    {activeTab === DETAIL_TAB.AVAILS && (
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Box
                                                    sx={{
                                                        display: "grid",
                                                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                                        gap: 2,
                                                        mb: 3,
                                                    }}
                                                >
                                                    {!canEdit ? (
                                                        <>
                                                            <ReadOnlyDetailField
                                                                label="License Type"
                                                                value={detail.license_type}
                                                            />
                                                            <ReadOnlyDetailField
                                                                label="Format Profile"
                                                                value={detail.format_profile}
                                                            />
                                                            <DetailCheckboxField
                                                                label="Branding Rights"
                                                                checked={isBrandingRightsChecked(
                                                                    detail.branding_rights
                                                                )}
                                                                disabled
                                                            />
                                                            <DetailCheckboxField
                                                                label="HDR10"
                                                                checked={isHdrChecked(detail.hdr)}
                                                                disabled
                                                            />
                                                            <ReadOnlyDetailField
                                                                label="License Rights Description"
                                                                value={displayOptionalSelectValue(
                                                                    detail.license_rights_description
                                                                )}
                                                                emptyDisplay="(blank)"
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                    <FormControl
                                                        size="small"
                                                        fullWidth
                                                        disabled={saving}
                                                        sx={detailDrawerFormControlSx}
                                                    >
                                                        <InputLabel>License Type</InputLabel>
                                                        <Select
                                                            label="License Type"
                                                            value={draft.license_type ?? detail.license_type}
                                                            onChange={(e) =>
                                                                setDraft((prev) => ({
                                                                    ...prev,
                                                                    license_type: String(e.target.value),
                                                                }))
                                                            }
                                                        >
                                                            {LICENSE_TYPE_OPTIONS.map((opt) => (
                                                                <MenuItem key={opt} value={opt}>
                                                                    {opt}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <FormControl
                                                        size="small"
                                                        fullWidth
                                                        disabled={saving}
                                                        sx={detailDrawerFormControlSx}
                                                    >
                                                        <InputLabel>Format Profile</InputLabel>
                                                        <Select
                                                            label="Format Profile"
                                                            value={draft.format_profile ?? detail.format_profile}
                                                            onChange={(e) =>
                                                                setDraft((prev) => ({
                                                                    ...prev,
                                                                    format_profile: String(e.target.value),
                                                                }))
                                                            }
                                                        >
                                                            {FORMAT_PROFILE_OPTIONS.map((opt) => (
                                                                <MenuItem key={opt} value={opt}>
                                                                    {opt}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                    <DetailCheckboxField
                                                        label="Branding Rights"
                                                        checked={isBrandingRightsChecked(
                                                            draft.branding_rights ?? detail.branding_rights
                                                        )}
                                                        disabled={saving}
                                                        onChange={(checked) =>
                                                            setDraft((prev) => ({
                                                                ...prev,
                                                                branding_rights: checked
                                                                    ? BRANDING_RIGHTS_CHECKED_VALUE
                                                                    : "",
                                                            }))
                                                        }
                                                    />
                                                    <DetailCheckboxField
                                                        label="HDR10"
                                                        checked={isHdrChecked(draft.hdr ?? detail.hdr)}
                                                        disabled={saving}
                                                        onChange={(checked) =>
                                                            setDraft((prev) => ({
                                                                ...prev,
                                                                hdr: checked ? HDR_CHECKED_VALUE : "",
                                                            }))
                                                        }
                                                    />
                                                    <FormControl
                                                        size="small"
                                                        fullWidth
                                                        disabled={saving}
                                                        sx={detailDrawerFormControlSx}
                                                    >
                                                        <InputLabel>License Rights Description</InputLabel>
                                                        <Select
                                                            label="License Rights Description"
                                                            value={
                                                                draft.license_rights_description ??
                                                                detail.license_rights_description
                                                            }
                                                            onChange={(e) =>
                                                                setDraft((prev) => ({
                                                                    ...prev,
                                                                    license_rights_description: String(e.target.value),
                                                                }))
                                                            }
                                                        >
                                                            {LICENSE_RIGHTS_OPTIONS.map((opt) => (
                                                                <MenuItem key={opt || "blank"} value={opt}>
                                                                    {opt || "(blank)"}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                        </>
                                                    )}
                                                </Box>

                                                <Typography variant="subtitle1" sx={{ mb: 1, ...detailDrawerSectionTitleSx }}>
                                                    License Windows
                                                </Typography>
                                                <TableContainer
                                                    component={Paper}
                                                    variant="outlined"
                                                    sx={{ mb: 3, ...detailDrawerTableSx }}
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Status</TableCell>
                                                                <TableCell>Playback Start</TableCell>
                                                                <TableCell>Playback End</TableCell>
                                                                <TableCell>Announce Date</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {detail.license_windows.map((window) => {
                                                                const windowKey = licenseWindowKey(window);
                                                                return (
                                                                <TableRow
                                                                    key={windowKey}
                                                                    sx={licenseWindowRowSx(window.status)}
                                                                >
                                                                    <TableCell sx={{ textTransform: "capitalize" }}>
                                                                        <ReadOnlyTableText>
                                                                            {window.status}
                                                                        </ReadOnlyTableText>
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {canEditLicenseWindowDateTime(
                                                                            canEdit,
                                                                            window.status
                                                                        ) ? (
                                                                            <TimePicker
                                                                                value={
                                                                                    playbackStartDrafts[windowKey] ??
                                                                                    null
                                                                                }
                                                                                onChange={(value) =>
                                                                                    setPlaybackStartDrafts((prev) => {
                                                                                        const next = applyPacificTimeOntoLockedDate(
                                                                                            window.playback_start_date,
                                                                                            value
                                                                                        );
                                                                                        if (!next) return prev;
                                                                                        return {
                                                                                            ...prev,
                                                                                            [windowKey]: next,
                                                                                        };
                                                                                    })
                                                                                }
                                                                                ampm
                                                                                timezone={AVAILS_PACIFIC_TZ}
                                                                                disabled={saving}
                                                                                slotProps={{
                                                                                    textField: {
                                                                                        size: "small",
                                                                                        fullWidth: true,
                                                                                        helperText: toPacificDayjs(
                                                                                            window.playback_start_date
                                                                                        )?.format("MM/DD/YYYY"),
                                                                                        sx: detailDrawerFormControlSx,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <ReadOnlyTableText
                                                                                muted={window.status === "past"}
                                                                            >
                                                                                {formatAvailsPacificDateTime(
                                                                                    window.playback_start_date
                                                                                )}
                                                                            </ReadOnlyTableText>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {canEditLicenseWindowDateTime(
                                                                            canEdit,
                                                                            window.status
                                                                        ) ? (
                                                                            <TimePicker
                                                                                value={
                                                                                    playbackEndDrafts[windowKey] ??
                                                                                    null
                                                                                }
                                                                                onChange={(value) =>
                                                                                    setPlaybackEndDrafts((prev) => ({
                                                                                        ...prev,
                                                                                        [windowKey]:
                                                                                            applyPacificTimeOntoLockedDate(
                                                                                                window.playback_end_date,
                                                                                                value
                                                                                            ),
                                                                                    }))
                                                                                }
                                                                                ampm
                                                                                timezone={AVAILS_PACIFIC_TZ}
                                                                                disabled={saving}
                                                                                slotProps={{
                                                                                    textField: {
                                                                                        size: "small",
                                                                                        fullWidth: true,
                                                                                        helperText: toPacificDayjs(
                                                                                            window.playback_end_date
                                                                                        )?.format("MM/DD/YYYY"),
                                                                                        sx: detailDrawerFormControlSx,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <ReadOnlyTableText
                                                                                muted={window.status === "past"}
                                                                            >
                                                                                {formatAvailsPacificDateTime(
                                                                                    window.playback_end_date
                                                                                )}
                                                                            </ReadOnlyTableText>
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {canEditLicenseWindowAnnounceDate(
                                                                            canEdit,
                                                                            window.status
                                                                        ) ? (
                                                                            <DateTimePicker
                                                                                value={
                                                                                    announceDrafts[windowKey] ??
                                                                                    null
                                                                                }
                                                                                onChange={(value) =>
                                                                                    setAnnounceDrafts((prev) => ({
                                                                                        ...prev,
                                                                                        [windowKey]: value,
                                                                                    }))
                                                                                }
                                                                                ampm
                                                                                timezone={AVAILS_PACIFIC_TZ}
                                                                                disabled={saving}
                                                                                slotProps={{
                                                                                    textField: {
                                                                                        size: "small",
                                                                                        fullWidth: true,
                                                                                        sx: detailDrawerFormControlSx,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <ReadOnlyTableText
                                                                                muted={window.status === "past"}
                                                                            >
                                                                                {formatAvailsPacificDateTime(
                                                                                    window.coming_soon_date
                                                                                )}
                                                                            </ReadOnlyTableText>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                                );
                                                            })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Box>
                                        </LocalizationProvider>
                                    )}

                                    {activeTab === DETAIL_TAB.ACTIVITY && (
                                        <TableContainer
                                            component={Paper}
                                            variant="outlined"
                                            sx={detailDrawerTableSx}
                                        >
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>When</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>User</TableCell>
                                                        <TableCell>Summary</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {detail.activity.length === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={4} align="center">
                                                                No activity recorded yet.
                                                            </TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        detail.activity.map((entry) => (
                                                            <TableRow key={entry.id}>
                                                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                                    <ReadOnlyTableText>
                                                                        {formatAvailsPacificDateTime(entry.timestamp)}
                                                                    </ReadOnlyTableText>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <ReadOnlyTableText>
                                                                        {ACTIVITY_TYPE_LABELS[entry.type] ?? entry.type}
                                                                    </ReadOnlyTableText>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <ReadOnlyTableText>
                                                                        {entry.actor || "—"}
                                                                    </ReadOnlyTableText>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <ReadOnlyTableText>
                                                                        {entry.summary || "—"}
                                                                    </ReadOnlyTableText>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>

                                {canEdit && activeTab === DETAIL_TAB.AVAILS && (
                                    <Box
                                        sx={{
                                            px: 3,
                                            py: 2,
                                            borderTop: 1,
                                            borderColor: AVAILS_DETAIL_DRAWER_COLORS.border,
                                            display: "flex",
                                            justifyContent: "flex-end",
                                            gap: 1,
                                            ...detailDrawerRootSx,
                                        }}
                                    >
                                        <Button onClick={onClose} disabled={saving || submittingToSlate || submittingToRoku}>
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={handleSave}
                                            disabled={saving || submittingToSlate || submittingToRoku || !hasUnsavedChanges()}
                                        >
                                            {saving ? "Saving…" : "Save changes"}
                                        </Button>
                                        {canSubmitToSlate && onSubmitToSlate && refId ? (
                                            <Button
                                                variant="outlined"
                                                onClick={() => void onSubmitToSlate(refId)}
                                                disabled={
                                                    saving ||
                                                    submittingToSlate ||
                                                    submittingToRoku ||
                                                    hasUnsavedChanges() ||
                                                    slateStatus === "synced"
                                                }
                                            >
                                                {submittingToSlate ? "Submitting…" : "Submit to Slate"}
                                            </Button>
                                        ) : null}
                                        {canSubmitToRoku && onSubmitToRoku && refId ? (
                                            <Button
                                                variant="outlined"
                                                onClick={() => void onSubmitToRoku(refId)}
                                                disabled={
                                                    saving ||
                                                    submittingToSlate ||
                                                    submittingToRoku ||
                                                    hasUnsavedChanges() ||
                                                    rokuStatus === "synced"
                                                }
                                            >
                                                {submittingToRoku ? "Submitting…" : "Submit to Roku"}
                                            </Button>
                                        ) : null}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ p: 3, ...detailDrawerRootSx }}>
                            <Typography sx={detailDrawerLabelSx}>Title not found.</Typography>
                        </Box>
                    )}
                </Box>
            </Drawer>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Apply changes to related assets?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Would you like to apply these title-level changes to all associated episodes,
                        trailers, and extras in this season?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <Button
                        variant="outlined"
                        onClick={() => void submitUpdate(true)}
                        disabled={saving}
                    >
                        Apply to all in season
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => void submitUpdate(false)}
                        disabled={saving}
                    >
                        This episode only
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
